import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { awcg_domain, awcg_domain_u1 } from "../../utils";
import { AnyLogo, AnyMark } from "../../components/AnyLogo";


const API = "https://render.anyweb.uk/api/v2/dashboard";
const ARROW = "\u2192";
const BACK = "\u2190";
const DOT = "\u00B7";

function fmt(n) { return n == null ? "\u2014" : n.toLocaleString("en-GB"); }
function fmtGBP(n) { return n == null ? "\u2014" : "\u00A3" + n.toLocaleString("en-GB"); }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "\u2014"; }

const Logo = ({ height = 24, dark = false }) => <AnyLogo height={height} dark={dark} />;

/* ── Category icons (monochrome, use currentColor) ── */
const iconProps = {
  width: 18, height: 18, viewBox: "0 0 24 24",
  fill: "none", stroke: "currentColor", strokeWidth: 1.8,
  strokeLinecap: "round", strokeLinejoin: "round",
};
const HouseIcon = () => (<svg {...iconProps}><path d="M3 10.5L12 3l9 7.5"/><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5"/></svg>);
const ShieldIcon = () => (<svg {...iconProps}><path d="M12 3l8 3v6c0 4.5-3.5 8-8 10-4.5-2-8-5.5-8-10V6l8-3z"/></svg>);
const WifiIcon = () => (<svg {...iconProps}><path d="M2 9a15 15 0 0 1 20 0"/><path d="M5.5 12.5a10 10 0 0 1 13 0"/><path d="M9 16a5 5 0 0 1 6 0"/><circle cx="12" cy="19" r="1" fill="currentColor" stroke="none"/></svg>);
const CapIcon = () => (<svg {...iconProps}><path d="M2 9l10-4 10 4-10 4L2 9z"/><path d="M6 11v4c0 1.5 3 3 6 3s6-1.5 6-3v-4"/></svg>);
const ForkIcon = () => (<svg {...iconProps}><path d="M8 3v8a2 2 0 0 1-2 2H5v8"/><path d="M10 3v6"/><path d="M6 3v6"/><path d="M17 3c-2 2-2 5 0 7v11"/></svg>);
const TrainIcon = () => (<svg {...iconProps}><rect x="5" y="3" width="14" height="14" rx="2"/><path d="M5 10h14"/><path d="M9 17l-2 4"/><path d="M15 17l2 4"/><circle cx="9" cy="13.5" r=".5" fill="currentColor" stroke="none"/><circle cx="15" cy="13.5" r=".5" fill="currentColor" stroke="none"/></svg>);
const DocIcon = () => (<svg {...iconProps}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6z"/><path d="M14 3v6h6"/><path d="M8 13h8"/><path d="M8 17h5"/></svg>);
const DropIcon = () => (<svg {...iconProps}><path d="M12 3c-3 4-6 7.5-6 11a6 6 0 0 0 12 0c0-3.5-3-7-6-11z"/></svg>);
const BriefcaseIcon = () => (<svg {...iconProps}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/><path d="M3 13h18"/></svg>);

/* ── Hash router ── */
function useHashRoute() {
  const [page, setPage] = useState(window.location.hash.replace("#/", "") || "");
  useEffect(() => {
    const h = () => setPage(window.location.hash.replace("#/", "") || "");
    window.addEventListener("hashchange", h);
    return () => window.removeEventListener("hashchange", h);
  }, []);
  const go = useCallback((p) => { window.location.hash = "/" + p; }, []);
  const home = useCallback(() => { window.location.hash = "/"; }, []);
  return { page, go, home };
}

/* ── Shared layout ── */
function Shell({ site, page, home, children, title }) {
  return (
    <div className="vd">
      <header className="vd-hdr">
        <div className="vd-hdr-in">
          <a className="vd-hdr-left" href="#/" onClick={(e) => { e.preventDefault(); home(); }}>
            <AnyMark size={22} />
            <span className="vd-hdr-domain">{site.hostname}</span>
          </a>
          <nav className="vd-hdr-nav">
            {[["Property", "property"], ["Schools", "schools"], ["Businesses", "businesses"], ["Compare", "compare"]].map(([label, slug]) => (
              <a key={slug} href={"#/" + slug} className={page === slug ? "active" : ""}>{label}</a>
            ))}
          </nav>
        </div>
      </header>
      {page && (
        <div className="vd-bread">
          <div className="vd-bread-in">
            <a href="#/" onClick={(e) => { e.preventDefault(); home(); }}>{BACK} Dashboard</a>
            <span>{title}</span>
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

/* ── Sparkline ── */
function Sparkline({ data, trend, color }) {
  if (!data || data.length < 2) return null;
  const w = 80, h = 24, pad = 2;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = (w - pad * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (h - pad * 2) * (1 - (v - min) / range);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const strokeColor = color || (trend >= 0 ? "#2563eb" : "#6b7280");
  return (
    <svg className="vd-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}

/* ── Verdict badge ── */
function Verdict({ verdict }) {
  if (!verdict) return null;
  const icon = verdict.magnitude === "good" ? "\u25B2" : verdict.magnitude === "bad" ? "\u25BC" : "\u2014";
  return (
    <span className={"vd-verdict vd-verdict-" + verdict.magnitude}>
      <span className="vd-verdict-icon">{icon}</span>
      <span>{verdict.pct > 0 ? "+" : ""}{verdict.pct}% {verdict.label}</span>
    </span>
  );
}

/* ── Widget card ── */
function Card({ label, value, sub, cta, onClick, verdict, spark, trend, icon: Icon }) {
  return (
    <div className="vd-card" onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className="vd-card-top">
        <div className="vd-card-label-wrap">
          {Icon && <span className="vd-card-icon"><Icon /></span>}
          <span className="vd-card-label">{label}</span>
        </div>
        {spark && spark.length >= 2 && (
          <div className="vd-card-spark">
            <Sparkline data={spark} trend={trend} />
          </div>
        )}
      </div>
      <span className="vd-card-value">{value}</span>
      {sub && <span className="vd-card-sub">{sub}</span>}
      {verdict && <Verdict verdict={verdict} />}
      {cta && <span className="vd-card-cta">{cta} {ARROW}</span>}
    </div>
  );
}

/* ── Detail page shell ── */
function DetailPage({ title, source, children }) {
  return (
    <div className="vd-detail">
      <div className="vd-detail-head">
        <h2>{title}</h2>
        {source && <p className="vd-detail-src">{source}</p>}
      </div>
      {children}
    </div>
  );
}

/* ── Dashboard grid ── */
function DashboardGrid({ data, go }) {
  const { site, widgets: w, tagline } = data;

  const keyFacts = [
    { label: "Population", value: site.population ? fmt(site.population) : null },
    { label: "Postcode", value: site.postcode_district },
    { label: "Region", value: site.region },
    { label: "Local authority", value: site.county },
  ].filter((f) => f.value);

  return (
    <div className="vd-home">
      <div className="vd-hero">
        <div className="vd-hero-eyebrow">{site.location_type ? site.location_type.replace(/_/g, " ") : "Place"} {DOT} {site.county || "England"}</div>
        <h1>{site.display_name}</h1>
        {tagline && <p className="vd-hero-tagline">{tagline}</p>}
        <div className="vd-hero-facts">
          {keyFacts.map((f) => (
            <div key={f.label} className="vd-hero-fact">
              <span className="vd-hero-fact-label">{f.label}</span>
              <span className="vd-hero-fact-value">{f.value}</span>
            </div>
          ))}
        </div>
        <div className="vd-hero-actions">
          <button className="vd-hero-btn" onClick={() => go("compare")}>
            Compare with another area {ARROW}
          </button>
        </div>
      </div>

      <div className="vd-section-label">At a glance</div>
      <div className="vd-grid">
        {w.property && (
          <Card
            icon={HouseIcon}
            label="Property"
            value={fmtGBP(w.property.medianPrice)}
            sub={`Median price ${DOT} ${w.property.salesCount} sales in 12 months`}
            verdict={w.property.vsUK}
            spark={w.property.spark}
            trend={w.property.trendPct}
            cta="View sales"
            onClick={() => go("property")}
          />
        )}
        {w.crime && (
          <Card
            icon={ShieldIcon}
            label="Crime"
            value={`${fmt(w.crime.rate || w.crime.totalIncidents)} / 1,000`}
            sub={`${fmt(w.crime.totalIncidents)} incidents in 12 months`}
            verdict={w.crime.vsUK}
            spark={w.crime.spark}
            trend={w.crime.trendPct}
            cta="View breakdown"
            onClick={() => go("crime")}
          />
        )}
        {w.broadband && (
          <Card
            icon={WifiIcon}
            label="Broadband"
            value={`${w.broadband.avg_download_mbps} Mbps`}
            sub={`Average download ${DOT} Up to ${w.broadband.max_download_mbps} Mbps`}
            verdict={w.broadband.vsUK}
            cta="View coverage"
            onClick={() => go("broadband")}
          />
        )}
        {w.schools && (
          <Card
            icon={CapIcon}
            label="Schools"
            value={`${w.schools.goodPct}% Good+`}
            sub={`${w.schools.goodOrBetter} of ${w.schools.total} schools rated Good or Outstanding`}
            verdict={w.schools.vsUK}
            cta="View schools"
            onClick={() => go("schools")}
          />
        )}
        {w.food && (
          <Card
            icon={ForkIcon}
            label="Food hygiene"
            value={`${w.food.fiveStarPct}% top-rated`}
            sub={`${w.food.fiveStarCount} of ${w.food.total} businesses rated 5-star`}
            verdict={w.food.vsUK}
            cta="View ratings"
            onClick={() => go("food")}
          />
        )}
        <Card
          icon={TrainIcon}
          label="Rail"
          value={"Ashford Int\u2019l"}
          sub={`30 minutes ${DOT} HS1 to London St Pancras`}
          cta="View connections"
          onClick={() => go("trains")}
        />
        {w.flood && (
          <Card
            icon={DropIcon}
            label="Flood risk"
            value={w.flood.river_sea_risk}
            sub={`${w.flood.nearest_watercourse} ${DOT} ${fmt(w.flood.distance_meters)}m from site`}
            cta="View assessment"
            onClick={() => go("flood")}
          />
        )}
        {w.companies && (
          <Card
            icon={BriefcaseIcon}
            label="Businesses"
            value={fmt(w.companies.activeCount)}
            sub={
              w.companies.topCategories?.length > 0
                ? `Top: ${w.companies.topCategories.slice(0, 3).map((c) => c.name).join(", ")}`
                : `${w.companies.perThousand || "\u2014"} per 1,000 residents`
            }
            cta="View directory"
            onClick={() => go("businesses")}
          />
        )}
        <Card
          icon={DocIcon}
          label="Planning"
          value="Active"
          sub="Applications via Rother District Council"
          cta="View details"
          onClick={() => go("planning")}
        />
      </div>
    </div>
  );
}

/* ── Detail: Property ── */
function PropertyDetail({ detail }) {
  const typeMap = { D: "Detached", S: "Semi", T: "Terrace", F: "Flat", O: "Other" };
  const PER_PAGE = 50;
  const [page, setPage] = useState(1);
  const sales = detail.propertySales || [];
  const totalPages = Math.max(1, Math.ceil(sales.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PER_PAGE;
  const rows = sales.slice(start, start + PER_PAGE);
  const firstRowRef = React.useRef(null);

  const goto = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    setPage(next);
    setTimeout(() => { firstRowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 0);
  };

  return (
    <DetailPage title="Property sales" source={`HM Land Registry Price Paid Data ${DOT} Crown copyright and database right`}>
      <div ref={firstRowRef} />
      <table className="vd-tbl">
        <thead><tr><th>Date</th><th>Price</th><th>Address</th><th>Type</th><th>Tenure</th></tr></thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={start + i}>
              <td className="vd-nowrap">{fmtDate(s.date)}</td>
              <td className="vd-num">{fmtGBP(s.price)}</td>
              <td>{[s.street, s.town].filter(Boolean).join(", ")}</td>
              <td>{s.type || typeMap[s.property_type] || "\u2014"}</td>
              <td>{s.tenure || "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {sales.length > PER_PAGE && (
        <div className="vd-pager">
          <button className="vd-pager-btn" onClick={() => goto(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
            {BACK} Previous
          </button>
          <span className="vd-pager-info">
            Page {currentPage} of {totalPages} {DOT} {fmt(sales.length)} sales
          </span>
          <button className="vd-pager-btn" onClick={() => goto(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
            Next {ARROW}
          </button>
        </div>
      )}
    </DetailPage>
  );
}

/* ── Detail: Crime ── */
function CrimeDetail({ detail }) {
  const [hoveredCat, setHoveredCat] = useState(null);
  const matrix = detail?.crimeMatrix || {};
  const hoveredRow = hoveredCat ? matrix[hoveredCat] || {} : null;
  const hoveredLabel = hoveredCat ? hoveredCat.replace(/-/g, " ") : null;

  return (
    <DetailPage title="Crime statistics" source={`data.police.uk ${DOT} Open Government Licence v3.0`}>
      <p className="vd-crime-hint">
        {hoveredLabel ? (
          <>Showing <strong className="vd-cap">{hoveredLabel}</strong> per month (of total). Move off to reset.</>
        ) : (
          <>Hover a category to see its monthly breakdown in the right-hand table.</>
        )}
      </p>
      <div className="vd-cols">
        <div>
          <h3 className="vd-col-title">By category</h3>
          <table className="vd-tbl vd-tbl-interactive">
            <thead><tr><th>Category</th><th>Count</th></tr></thead>
            <tbody onMouseLeave={() => setHoveredCat(null)}>
              {detail.crimeCategories?.map((c) => (
                <tr
                  key={c.category}
                  className={hoveredCat === c.category ? "vd-row-active" : ""}
                  onMouseEnter={() => setHoveredCat(c.category)}
                >
                  <td className="vd-cap">{c.category.replace(/-/g, " ")}</td>
                  <td className="vd-num">{fmt(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3 className="vd-col-title">By month {hoveredCat && <span className="vd-col-title-sub"> {"\u2014"} {hoveredLabel}</span>}</h3>
          <table className="vd-tbl">
            <thead><tr><th>Month</th><th>{hoveredCat ? "Count / Total" : "Count"}</th></tr></thead>
            <tbody>
              {detail.crimeByMonth?.map((m) => {
                const catCount = hoveredRow ? (hoveredRow[m.month] || 0) : null;
                return (
                  <tr key={m.month}>
                    <td>{m.month}</td>
                    <td className="vd-num">
                      {hoveredCat ? (
                        <>
                          <strong className="vd-frac-num">{fmt(catCount)}</strong>
                          <span className="vd-frac-sep">/</span>
                          <span className="vd-frac-total">{fmt(m.count)}</span>
                        </>
                      ) : (
                        fmt(m.count)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DetailPage>
  );
}

/* ── Detail: Schools ── */
function SchoolsDetail({ detail }) {
  return (
    <DetailPage title="Schools" source="Department for Education / Ofsted">
      <div className="vd-school-grid">
        {detail.schools?.map((s, i) => (
          <div key={i} className="vd-school-card">
            <h3>{s.name}</h3>
            <div className="vd-school-meta">
              <span>{s.phase}</span>
              {s.ofsted_rating && <span className={"vd-ofsted vd-ofsted-" + s.ofsted_rating.toLowerCase().replace(/ /g, "")}>{s.ofsted_rating}</span>}
            </div>
            <div className="vd-school-stats">
              {s.pupil_count && <span>{fmt(s.pupil_count)} pupils</span>}
              {s.type_of_school && <span>{s.type_of_school}</span>}
              {s.postcode && <span>{s.postcode}</span>}
            </div>
          </div>
        ))}
      </div>
    </DetailPage>
  );
}

/* ── Detail: Food ── */
function FoodDetail({ detail }) {
  return (
    <DetailPage title="Food hygiene ratings" source="Food Standards Agency">
      {detail.foodBreakdown && (
        <div className="vd-food-bar">
          {detail.foodBreakdown.map((r) => (
            <div key={r.rating} className="vd-food-seg">
              <span className={"vd-food-dot vd-food-dot-" + r.rating} />
              <span className="vd-food-rating">Rating {r.rating}</span>
              <span className="vd-food-count">{r.count}</span>
            </div>
          ))}
        </div>
      )}
      <table className="vd-tbl">
        <thead><tr><th>Business</th><th>Type</th><th>Rating</th><th>Inspected</th></tr></thead>
        <tbody>
          {detail.foodBusinesses?.slice(0, 80).map((f, i) => (
            <tr key={i}>
              <td>{f.business_name}</td>
              <td className="vd-muted">{f.business_type}</td>
              <td><span className={"vd-pill vd-pill-" + f.rating}>{f.rating}</span></td>
              <td className="vd-muted">{fmtDate(f.rating_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DetailPage>
  );
}

/* ── Detail: Broadband ── */
function BroadbandDetail({ widgets }) {
  const b = widgets.broadband;
  if (!b) return null;
  return (
    <DetailPage title="Broadband coverage" source="Ofcom Connected Nations report">
      <div className="vd-stat-row">
        <div className="vd-stat"><span className="vd-stat-n">{b.avg_download_mbps}</span><span className="vd-stat-u">Mbps average</span></div>
        <div className="vd-stat"><span className="vd-stat-n">{b.max_download_mbps}</span><span className="vd-stat-u">Mbps maximum</span></div>
        <div className="vd-stat"><span className="vd-stat-n">{b.fibre_available ? "Yes" : "No"}</span><span className="vd-stat-u">Fibre available</span></div>
        <div className="vd-stat"><span className="vd-stat-n">{b.ultrafast_available ? "Yes" : "No"}</span><span className="vd-stat-u">Ultrafast available</span></div>
      </div>
    </DetailPage>
  );
}

/* ── Detail: Flood ── */
function FloodDetail({ widgets }) {
  const f = widgets.flood;
  if (!f) return null;
  return (
    <DetailPage title="Flood risk assessment" source="Environment Agency">
      <div className="vd-stat-row">
        <div className="vd-stat"><span className="vd-stat-n">{f.river_sea_risk}</span><span className="vd-stat-u">River &amp; sea risk</span></div>
        <div className="vd-stat"><span className="vd-stat-n">{f.surface_water_risk}</span><span className="vd-stat-u">Surface water risk</span></div>
        <div className="vd-stat"><span className="vd-stat-n">{f.nearest_watercourse}</span><span className="vd-stat-u">Nearest watercourse</span></div>
        <div className="vd-stat"><span className="vd-stat-n">{fmt(f.distance_meters)}m</span><span className="vd-stat-u">Distance</span></div>
      </div>
      <div className="vd-panel">
        <a href="https://check-long-term-flood-risk.service.gov.uk/postcode" target="_blank" rel="noopener noreferrer">Check your property's flood risk on GOV.UK {ARROW}</a>
      </div>
    </DetailPage>
  );
}

/* ── Detail: Trains ── */
function DepartureBoard({ crs, stationName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = () => {
      fetch(`https://render.anyweb.uk/api/v2/departures?station=${crs}`)
        .then((r) => r.json())
        .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
        .catch(() => { if (!cancelled) { setData({ error: "fetch_failed" }); setLoading(false); } });
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [crs]);

  if (loading) return <div className="vd-deps-loading">Loading live departures for {stationName}...</div>;
  if (data?.error) {
    return (
      <div className="vd-deps-empty">
        <strong>Live departures unavailable</strong>
        <p>{data.message || "Departure board will activate once National Rail API token is configured."}</p>
      </div>
    );
  }
  if (!data?.services?.length) {
    return <div className="vd-deps-empty"><p>No upcoming departures from {stationName}.</p></div>;
  }

  return (
    <div className="vd-deps">
      <div className="vd-deps-head">
        <h3>Live departures {DOT} {stationName}</h3>
        <span className="vd-deps-time">Updated {fmtTime(data.fetchedAt)}</span>
      </div>
      <table className="vd-tbl">
        <thead><tr><th>Scheduled</th><th>Expected</th><th>Destination</th><th>Platform</th><th>Operator</th></tr></thead>
        <tbody>
          {data.services.map((s, i) => (
            <tr key={i}>
              <td className="vd-num">{s.std || "\u2014"}</td>
              <td className={"vd-num " + (s.etd === "On time" ? "vd-ontime" : s.etd === "Cancelled" ? "vd-cancelled" : "vd-delayed")}>{s.etd || "\u2014"}</td>
              <td>{s.destination || "\u2014"}</td>
              <td className="vd-num">{s.platform || "\u2014"}</td>
              <td className="vd-muted">{s.operator || "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/* Schematic positions for Rye-area rail network (SBB-style topological map) */
const SCHEMATIC = {
  RYE: { x: 360, y: 200, line: "marshlink", labelBelow: true, hub: true },
  WSE: { x: 230, y: 200, line: "marshlink", labelBelow: true },
  HGS: { x: 100, y: 200, line: "marshlink", labelBelow: true, terminus: true },
  APD: { x: 490, y: 200, line: "marshlink", labelBelow: true },
  HMT: { x: 620, y: 200, line: "marshlink", labelBelow: true },
  AFK: { x: 750, y: 200, line: "interchange", labelBelow: true, interchange: true },
  STP: { x: 750, y: 70, line: "hs1", labelBelow: false, terminus: true },
};

const LINE_COLORS = {
  marshlink: "#2563eb",
  hs1: "#0a0a0a",
};

function RailSchematic({ stations, selectedCrs, onSelect }) {
  const byCrs = Object.fromEntries(stations.map((s) => [s.crs, s]));
  const positioned = stations.filter((s) => SCHEMATIC[s.crs]);

  return (
    <div className="vd-rail-map">
      <svg viewBox="0 0 850 280" xmlns="http://www.w3.org/2000/svg" className="vd-rail-svg" role="img" aria-label="Rail network schematic">
        {/* Marshlink line: HGS → AFK */}
        <line x1={SCHEMATIC.HGS.x} y1={SCHEMATIC.HGS.y} x2={SCHEMATIC.AFK.x} y2={SCHEMATIC.AFK.y} stroke={LINE_COLORS.marshlink} strokeWidth="5" strokeLinecap="round" />
        {/* HS1: AFK → STP */}
        <line x1={SCHEMATIC.AFK.x} y1={SCHEMATIC.AFK.y} x2={SCHEMATIC.STP.x} y2={SCHEMATIC.STP.y} stroke={LINE_COLORS.hs1} strokeWidth="5" strokeLinecap="round" />

        {/* Line labels */}
        <text x="170" y="178" className="vd-rail-line-label" fill={LINE_COLORS.marshlink}>MARSHLINK LINE</text>
        <text x="770" y="140" className="vd-rail-line-label" fill={LINE_COLORS.hs1} textAnchor="start">HS1</text>

        {/* Stations */}
        {positioned.map((s) => {
          const p = SCHEMATIC[s.crs];
          const isSelected = s.crs === selectedCrs;
          const isHub = p.hub;
          const r = isHub ? 11 : p.interchange ? 9 : 7;
          const fill = isSelected ? "#2563eb" : isHub ? "#0a0a0a" : "#fff";
          const stroke = isSelected ? "#2563eb" : "#0a0a0a";

          const labelY = p.labelBelow ? p.y + 28 : p.y - 16;
          const metaY = p.labelBelow ? p.y + 44 : p.y - 32;

          return (
            <g key={s.crs} className="vd-rail-station" onClick={() => onSelect(s.crs)} style={{ cursor: "pointer" }}>
              {/* Halo for hover/selection */}
              <circle cx={p.x} cy={p.y} r={r + 8} fill="transparent" />
              <circle cx={p.x} cy={p.y} r={r} fill={fill} stroke={stroke} strokeWidth="3" />
              <text x={p.x} y={labelY} className={"vd-rail-station-name" + (isSelected ? " selected" : "")} textAnchor="middle">
                {s.name}
              </text>
              <text x={p.x} y={metaY} className="vd-rail-station-meta" textAnchor="middle">
                {s.journeyMins === 0 ? "Home" : `${s.journeyMins} min`}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function TrainsDetail({ site, detail }) {
  const stations = detail?.stations || [];
  const homeStation = stations.find((s) => s.journeyMins === 0) || stations[0];
  const [selectedCrs, setSelectedCrs] = useState(homeStation?.crs || "RYE");
  const selected = stations.find((s) => s.crs === selectedCrs);

  return (
    <DetailPage title="Rail connections" source={`Nearest stations to ${site.display_name}`}>
      {stations.length > 0 && <RailSchematic stations={stations} selectedCrs={selectedCrs} onSelect={setSelectedCrs} />}

      <div className="vd-station-list">
        {stations.map((s) => (
          <button key={s.crs} className={"vd-station-btn" + (s.crs === selectedCrs ? " active" : "")} onClick={() => setSelectedCrs(s.crs)}>
            <span className="vd-station-name">{s.name}</span>
            <span className="vd-station-meta">{s.journeyMins === 0 ? "Home" : `${s.journeyMins} min`} {DOT} {s.operator}</span>
          </button>
        ))}
      </div>

      {selected && <DepartureBoard crs={selected.crs} stationName={selected.name} />}
    </DetailPage>
  );
}

/* ── Detail: Planning ── */
function PlanningDetail({ site }) {
  return (
    <DetailPage title="Planning applications" source="Rother District Council">
      <div className="vd-panel">
        <p>Planning applications for the {site.postcode_district} area are managed by Rother District Council.</p>
        <a href="https://planweb01.rother.gov.uk/OcellaWeb/planningSearch" target="_blank" rel="noopener noreferrer">Search planning applications {ARROW}</a>
      </div>
    </DetailPage>
  );
}

/* ── Detail: Businesses ── */
function BusinessesDetail({ site, widgets, detail }) {
  const c = widgets.companies || {};
  const breakdown = detail?.companiesCategoryBreakdown || [];
  const topCat = c.topCategories?.[0];

  return (
    <DetailPage title="Local businesses" source={`Companies House ${DOT} Crown copyright`}>
      <div className="vd-stat-row">
        <div className="vd-stat">
          <span className="vd-stat-n">{fmt(c.activeCount || 0)}</span>
          <span className="vd-stat-u">Active companies</span>
        </div>
        <div className="vd-stat">
          <span className="vd-stat-n">{topCat?.name || "\u2014"}</span>
          <span className="vd-stat-u">Top sector {topCat ? `\u00B7 ${fmt(topCat.count)} companies` : ""}</span>
        </div>
        <div className="vd-stat">
          <span className="vd-stat-n">{fmt(c.perThousand || 0)}</span>
          <span className="vd-stat-u">Per 1,000 residents</span>
        </div>
      </div>

      {breakdown.length > 0 && (
        <>
          <h3 className="vd-col-title" style={{ marginTop: "2rem" }}>Sector breakdown</h3>
          <table className="vd-tbl">
            <thead><tr><th>Sector</th><th>Companies</th><th>Share</th></tr></thead>
            <tbody>
              {breakdown.map((b, i) => (
                <tr key={i}>
                  <td>{b.name}</td>
                  <td className="vd-num">{fmt(b.count)}</td>
                  <td className="vd-num">{Math.round((b.count / c.activeCount) * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="vd-panel" style={{ marginTop: "2rem" }}>
        <p>
          <a href={`https://find-and-update.company-information.service.gov.uk/advanced-search/get-results?registeredOfficeAddress=${encodeURIComponent(site.postcode_district)}&company-status-active=1`} target="_blank" rel="noopener noreferrer">
            Search live on Companies House {ARROW}
          </a>
        </p>
      </div>
    </DetailPage>
  );
}

/* ── Footer ── */
function Footer({ site }) {
  return (
    <footer className="vd-ft">
      <div className="vd-ft-in">
        <div className="vd-ft-logo"><Logo height={28} /></div>
        <p className="vd-ft-attr">Data: Land Registry, police.uk, Ofsted, FSA, Ofcom, Environment Agency, Companies House</p>
        <p className="vd-ft-legal">Contains public sector information licensed under the Open Government Licence v3.0</p>
        <p className="vd-ft-copy">{"\u00A9"} {new Date().getFullYear()} {site.hostname}</p>
      </div>
    </footer>
  );
}

/* ── Compare mode ── */
const COMPARE_OPTIONS = [
  { hostname: "hastings.co.uk", label: "Hastings" },
  { hostname: "ashford.co.uk", label: "Ashford" },
  { hostname: "brighton.co.uk", label: "Brighton" },
];

function CompareRow({ label, left, right, format = (v) => v, verdict }) {
  const leftVal = left == null ? "\u2014" : format(left);
  const rightVal = right == null ? "\u2014" : format(right);
  return (
    <tr className="vd-cmp-row">
      <td className="vd-cmp-label">{label}</td>
      <td className={"vd-cmp-cell" + (verdict === "left" ? " vd-cmp-win" : "")}>{leftVal}</td>
      <td className={"vd-cmp-cell" + (verdict === "right" ? " vd-cmp-win" : "")}>{rightVal}</td>
    </tr>
  );
}

function CompareMode({ current }) {
  const [otherHost, setOtherHost] = useState(COMPARE_OPTIONS[0].hostname);
  const [other, setOther] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}?hostname=${otherHost}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setOther(d); setLoading(false); })
      .catch(() => { setOther(null); setLoading(false); });
  }, [otherHost]);

  const L = current;
  const R = other;
  const Lw = L?.widgets || {};
  const Rw = R?.widgets || {};

  const cmpLow = (a, b) => (a != null && b != null ? (a < b ? "left" : a > b ? "right" : null) : null);
  const cmpHigh = (a, b) => (a != null && b != null ? (a > b ? "left" : a < b ? "right" : null) : null);

  return (
    <DetailPage title="Compare locations" source="Side-by-side decision view">
      <div className="vd-cmp-select">
        <span className="vd-cmp-select-label">Compare {L.site.display_name} with</span>
        <select value={otherHost} onChange={(e) => setOtherHost(e.target.value)} className="vd-cmp-dropdown">
          {COMPARE_OPTIONS.map((o) => (
            <option key={o.hostname} value={o.hostname}>{o.label}</option>
          ))}
        </select>
      </div>

      <table className="vd-cmp-tbl">
        <thead>
          <tr>
            <th></th>
            <th>{L.site.display_name}</th>
            <th>{loading ? "Loading\u2026" : R?.site?.display_name || "\u2014"}</th>
          </tr>
        </thead>
        <tbody>
          <CompareRow label="Population" left={L.site.population} right={R?.site?.population} format={(v) => fmt(v)} verdict={cmpHigh(L.site.population, R?.site?.population)} />
          <CompareRow label="Postcode" left={L.site.postcode_district} right={R?.site?.postcode_district} />
          <CompareRow label="Region" left={L.site.region} right={R?.site?.region} />
          <tr className="vd-cmp-section"><td colSpan="3">Property</td></tr>
          <CompareRow label="Median price" left={Lw.property?.medianPrice} right={Rw.property?.medianPrice} format={fmtGBP} verdict={cmpLow(Lw.property?.medianPrice, Rw.property?.medianPrice)} />
          <CompareRow label="Sales (12 mo)" left={Lw.property?.salesCount} right={Rw.property?.salesCount} format={fmt} />
          <tr className="vd-cmp-section"><td colSpan="3">Safety</td></tr>
          <CompareRow label="Crime per 1,000" left={Lw.crime?.rate} right={Rw.crime?.rate} format={fmt} verdict={cmpLow(Lw.crime?.rate, Rw.crime?.rate)} />
          <CompareRow label="Total incidents" left={Lw.crime?.totalIncidents} right={Rw.crime?.totalIncidents} format={fmt} />
          <tr className="vd-cmp-section"><td colSpan="3">Connectivity</td></tr>
          <CompareRow label="Broadband avg" left={Lw.broadband?.avg_download_mbps} right={Rw.broadband?.avg_download_mbps} format={(v) => v + " Mbps"} verdict={cmpHigh(Lw.broadband?.avg_download_mbps, Rw.broadband?.avg_download_mbps)} />
          <CompareRow label="Fibre available" left={Lw.broadband?.fibre_available ? "Yes" : "No"} right={Rw.broadband ? (Rw.broadband.fibre_available ? "Yes" : "No") : null} />
          <tr className="vd-cmp-section"><td colSpan="3">Education</td></tr>
          <CompareRow label="Schools nearby" left={Lw.schools?.total} right={Rw.schools?.total} format={fmt} verdict={cmpHigh(Lw.schools?.total, Rw.schools?.total)} />
          <CompareRow label="Good+ rating" left={Lw.schools?.goodPct} right={Rw.schools?.goodPct} format={(v) => v + "%"} verdict={cmpHigh(Lw.schools?.goodPct, Rw.schools?.goodPct)} />
          <tr className="vd-cmp-section"><td colSpan="3">Amenities</td></tr>
          <CompareRow label="Food 5-star" left={Lw.food?.fiveStarPct} right={Rw.food?.fiveStarPct} format={(v) => v + "%"} verdict={cmpHigh(Lw.food?.fiveStarPct, Rw.food?.fiveStarPct)} />
          <CompareRow label="Businesses" left={Lw.companies?.activeCount} right={Rw.companies?.activeCount} format={fmt} verdict={cmpHigh(Lw.companies?.activeCount, Rw.companies?.activeCount)} />
          <tr className="vd-cmp-section"><td colSpan="3">Environment</td></tr>
          <CompareRow label="Flood risk" left={Lw.flood?.river_sea_risk} right={Rw.flood?.river_sea_risk} />
        </tbody>
      </table>

      <p className="vd-cmp-note">Green highlights the better value for each row. Some data is still being collected for comparison locations.</p>
    </DetailPage>
  );
}

/* ── Main component ── */
const VillageDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { page, go, home } = useHashRoute();

  useEffect(() => {
    fetch(`${API}?hostname=${awcg_domain}`)
      .then((r) => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  const title = data ? `${data.site.display_name} \u2014 Local Data & Information | ${awcg_domain}` : `${awcg_domain_u1}`;
  const desc = data ? `Local data for ${data.site.display_name}. Property prices, crime, schools, food hygiene, broadband, flood risk, businesses.` : "";

  if (error) return <div className="vd-empty">Unable to load data.</div>;
  if (!data) return <div className="vd-empty">Loading...</div>;

  const { site, widgets, detail } = data;
  const pages = {
    "": <DashboardGrid data={data} go={go} />,
    property: <PropertyDetail detail={detail} />,
    crime: <CrimeDetail detail={detail} />,
    schools: <SchoolsDetail detail={detail} />,
    food: <FoodDetail detail={detail} />,
    broadband: <BroadbandDetail widgets={widgets} />,
    trains: <TrainsDetail site={site} detail={detail} />,
    planning: <PlanningDetail site={site} />,
    flood: <FloodDetail widgets={widgets} />,
    businesses: <BusinessesDetail site={site} widgets={widgets} detail={detail} />,
    compare: <CompareMode current={data} />,
  };

  const pageTitle = { property: "Property", crime: "Crime", schools: "Schools", food: "Food hygiene", broadband: "Broadband", trains: "Rail", planning: "Planning", flood: "Flood risk", businesses: "Businesses", compare: "Compare" };

  return (
    <>
      <Helmet>
        <title>{page ? `${pageTitle[page]} \u2014 ${site.display_name}` : title}</title>
        {desc && <meta name="description" content={desc} />}
        <link rel="canonical" href={`https://${awcg_domain}/`} />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />
        <meta property="og:title" content={title} />
        {desc && <meta property="og:description" content={desc} />}
        <meta property="og:url" content={`https://${awcg_domain}/`} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <style>{CSS}</style>
      <Shell site={site} page={page} home={home} title={pageTitle[page]}>
        <main className="vd-main">{pages[page] || pages[""]}</main>
        <Footer site={site} />
      </Shell>
    </>
  );
};

/* ── Styles ── */
const CSS = `
.vd{--bg:#faf9f6;--card:#fff;--ink:#111113;--muted:#6b7280;--subtle:#9ca3af;--line:#ececec;--line-soft:#f1f0ea;--accent:#2563eb;--accent-soft:#eff4ff;--radius:12px;--shadow:0 1px 2px rgba(16,24,40,.04),0 4px 12px rgba(16,24,40,.04);--shadow-lift:0 2px 4px rgba(16,24,40,.05),0 12px 32px rgba(16,24,40,.08);margin:0;padding:0;font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;color:var(--ink);background:var(--bg);min-height:100vh;display:flex;flex-direction:column;font-feature-settings:"cv11","ss01","ss03"}
.vd *{box-sizing:border-box;margin:0;padding:0}

/* Header */
.vd-hdr{background:rgba(255,255,255,.8);backdrop-filter:saturate(180%) blur(12px);-webkit-backdrop-filter:saturate(180%) blur(12px);border-bottom:1px solid var(--line);padding:0 2rem;position:sticky;top:0;z-index:10}
.vd-hdr-in{max-width:1120px;margin:0 auto;height:60px;display:flex;justify-content:space-between;align-items:center}
.vd-hdr-left{display:flex;align-items:center;gap:.6rem;text-decoration:none;color:inherit;transition:opacity .15s}
.vd-hdr-left:hover{opacity:.75}
.vd-hdr-domain{font-size:1rem;font-weight:600;color:var(--ink);letter-spacing:-.01em}
.vd-hdr-nav{display:flex;gap:1.75rem}
.vd-hdr-nav a{font-size:.85rem;font-weight:500;color:var(--muted);text-decoration:none;transition:color .15s}
.vd-hdr-nav a:hover,.vd-hdr-nav a.active{color:var(--ink)}

/* Breadcrumb */
.vd-bread{background:var(--bg);border-bottom:1px solid var(--line-soft);padding:0 2rem}
.vd-bread-in{max-width:1120px;margin:0 auto;height:44px;display:flex;align-items:center;gap:.5rem;font-size:.82rem;color:var(--subtle);font-weight:500}
.vd-bread-in a{color:var(--muted);text-decoration:none;transition:color .15s;display:inline-flex;align-items:center;gap:.25rem}
.vd-bread-in a:hover{color:var(--ink)}

/* Main */
.vd-main{max-width:1120px;margin:0 auto;padding:3rem 2rem;width:100%;flex:1}

/* Hero */
.vd-hero{margin-bottom:2.5rem;padding-bottom:2rem;border-bottom:1px solid var(--line)}
.vd-hero-eyebrow{font-size:.72rem;text-transform:uppercase;letter-spacing:.08em;color:var(--accent);font-weight:600;margin-bottom:.75rem}
.vd-hero h1{font-size:clamp(2.25rem,5vw,3.25rem);font-weight:700;letter-spacing:-.028em;line-height:1.05;color:var(--ink)}
.vd-hero-tagline{font-size:clamp(1rem,1.5vw,1.15rem);color:var(--muted);margin-top:1rem;line-height:1.55;max-width:680px;font-weight:400}
.vd-hero-facts{display:flex;flex-wrap:wrap;gap:2.5rem;margin-top:1.75rem}
.vd-hero-fact{display:flex;flex-direction:column;gap:.2rem}
.vd-hero-fact-label{font-size:.68rem;text-transform:uppercase;letter-spacing:.08em;color:var(--subtle);font-weight:600}
.vd-hero-fact-value{font-size:1.05rem;color:var(--ink);font-weight:600;letter-spacing:-.01em}

.vd-hero-actions{margin-top:1.75rem}
.vd-hero-btn{background:var(--ink);color:#fff;border:none;border-radius:8px;padding:.7rem 1.1rem;font-size:.85rem;font-weight:600;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:.35rem;transition:background .15s,transform .15s}
.vd-hero-btn:hover{background:#000;transform:translateY(-1px)}

/* Compare mode */
.vd-cmp-select{display:flex;align-items:center;gap:.75rem;margin-bottom:1.5rem;padding:1rem 1.25rem;background:var(--card);border:1px solid var(--line);border-radius:var(--radius);box-shadow:var(--shadow)}
.vd-cmp-select-label{font-size:.9rem;color:var(--muted);font-weight:500}
.vd-cmp-dropdown{font-family:inherit;font-size:.9rem;font-weight:600;color:var(--ink);background:var(--bg);border:1px solid var(--line);border-radius:6px;padding:.45rem .7rem;cursor:pointer}
.vd-cmp-tbl{width:100%;border-collapse:collapse;background:var(--card);border:1px solid var(--line);border-radius:var(--radius);overflow:hidden;box-shadow:var(--shadow)}
.vd-cmp-tbl th{text-align:left;padding:1rem 1.25rem;font-size:.75rem;text-transform:uppercase;letter-spacing:.07em;color:var(--subtle);font-weight:600;background:var(--bg);border-bottom:1px solid var(--line)}
.vd-cmp-tbl th:nth-child(2),.vd-cmp-tbl th:nth-child(3){font-size:.95rem;text-transform:none;letter-spacing:-.01em;color:var(--ink);font-weight:700}
.vd-cmp-label{padding:.75rem 1.25rem;font-size:.85rem;color:var(--muted);font-weight:500;width:30%}
.vd-cmp-cell{padding:.75rem 1.25rem;font-size:.95rem;font-weight:600;color:var(--ink);border-bottom:1px solid var(--line-soft);font-variant-numeric:tabular-nums}
.vd-cmp-win{background:#ecfdf5;color:#059669;position:relative}
.vd-cmp-win::before{content:"\u25BC";position:absolute;right:.6rem;top:50%;transform:translateY(-50%) rotate(180deg);font-size:.55rem;opacity:.5}
.vd-cmp-section td{padding:.85rem 1.25rem .45rem;font-size:.7rem;text-transform:uppercase;letter-spacing:.08em;color:var(--subtle);font-weight:600;background:var(--bg);border-bottom:1px solid var(--line)}
.vd-cmp-row td{border-bottom:1px solid var(--line-soft)}
.vd-cmp-note{font-size:.78rem;color:var(--subtle);margin-top:1rem;line-height:1.6}

/* Section label */
.vd-section-label{font-size:.72rem;text-transform:uppercase;letter-spacing:.08em;color:var(--subtle);font-weight:600;margin-bottom:1rem;display:inline-flex;align-items:center;gap:.5rem}
.vd-section-label::before{content:"";display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--accent)}

/* Verdict badge */
.vd-verdict{display:inline-flex;align-items:center;gap:.3rem;margin-top:.5rem;padding:.25rem .55rem;border-radius:6px;font-size:.72rem;font-weight:600;line-height:1.3;align-self:flex-start;max-width:100%}
.vd-verdict-icon{font-size:.62rem;line-height:1}
.vd-verdict-good{background:#ecfdf5;color:#059669}
.vd-verdict-bad{background:#fef2f2;color:#dc2626}
.vd-verdict-neutral{background:#f3f4f6;color:#6b7280}

/* Card grid */
.vd-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem}
.vd-card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.5rem;display:flex;flex-direction:column;gap:.4rem;cursor:pointer;transition:box-shadow .2s,transform .2s,border-color .2s;position:relative}
.vd-card:hover{box-shadow:var(--shadow-lift);transform:translateY(-2px);border-color:transparent}
.vd-card-top{display:flex;justify-content:space-between;align-items:center;gap:.5rem}
.vd-card-label-wrap{display:flex;align-items:center;gap:.55rem;min-width:0}
.vd-card-icon{display:inline-flex;align-items:center;justify-content:center;color:var(--subtle);transition:color .2s ease}
.vd-card:hover .vd-card-icon{color:var(--muted)}
.vd-card-spark{color:var(--accent);opacity:.7}
.vd-card-label{font-size:.72rem;text-transform:uppercase;letter-spacing:.07em;color:var(--subtle);font-weight:600}
.vd-card-value{font-size:1.85rem;font-weight:700;color:var(--ink);line-height:1.15;letter-spacing:-.02em;margin-top:.15rem}
.vd-card-sub{font-size:.825rem;color:var(--muted);margin-top:.1rem;line-height:1.4}
.vd-card-cta{margin-top:auto;padding-top:1.1rem;font-size:.8rem;font-weight:600;color:var(--accent);display:inline-flex;align-items:center;gap:.3rem;transition:gap .2s}
.vd-card:hover .vd-card-cta{gap:.5rem}

/* Detail pages */
.vd-detail{min-height:60vh}
.vd-detail-head{margin-bottom:2rem}
.vd-detail-head h2{font-size:clamp(1.5rem,3.2vw,2rem);font-weight:700;letter-spacing:-.02em;color:var(--ink)}
.vd-detail-src{font-size:.78rem;color:var(--subtle);margin-top:.35rem;font-weight:500}

/* Tables */
.vd-tbl{width:100%;border-collapse:collapse;font-size:.88rem}
.vd-tbl th{text-align:left;padding:.75rem .85rem;border-bottom:1px solid var(--line);font-weight:600;font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:var(--subtle);background:var(--bg)}
.vd-tbl td{padding:.75rem .85rem;border-bottom:1px solid var(--line-soft);color:#333}
.vd-tbl tbody tr:hover{background:var(--bg)}
.vd-num{text-align:right;font-variant-numeric:tabular-nums;font-weight:500}
.vd-nowrap{white-space:nowrap}
.vd-muted{color:var(--muted)}
.vd-cap{text-transform:capitalize}
.vd-more{font-size:.8rem;color:var(--subtle);margin-top:1rem}
.vd-pager{display:flex;justify-content:space-between;align-items:center;gap:1rem;margin-top:1.5rem;padding:1rem 0;border-top:1px solid var(--line-soft)}
.vd-pager-info{font-size:.82rem;color:var(--muted);font-weight:500;font-variant-numeric:tabular-nums}
.vd-pager-btn{font-family:inherit;font-size:.82rem;font-weight:600;color:var(--ink);background:var(--card);border:1px solid var(--line);border-radius:8px;padding:.55rem 1.1rem;cursor:pointer;transition:all .15s;display:inline-flex;align-items:center;gap:.3rem}
.vd-pager-btn:hover:not(:disabled){border-color:var(--accent);color:var(--accent)}
.vd-pager-btn:disabled{opacity:.4;cursor:not-allowed}

/* Columns */
.vd-cols{display:grid;grid-template-columns:1fr 1fr;gap:3rem}
.vd-col-title{font-size:.75rem;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--subtle);margin-bottom:1rem}
.vd-col-title-sub{color:var(--accent);font-weight:600;text-transform:none;letter-spacing:0;font-size:.8rem}
.vd-crime-hint{font-size:.8rem;color:var(--muted);margin-bottom:1rem;min-height:1.2rem}
.vd-crime-hint strong{color:var(--accent);font-weight:600}
.vd-tbl-interactive tbody tr{cursor:pointer;transition:background .12s}
.vd-tbl-interactive tbody tr.vd-row-active{background:var(--accent-soft)}
.vd-tbl-interactive tbody tr.vd-row-active td{color:var(--ink)}
.vd-frac-num{font-weight:700;color:var(--accent)}
.vd-frac-sep{color:var(--subtle);margin:0 .2rem;font-weight:400}
.vd-frac-total{color:var(--muted);font-weight:500}

/* Stat row */
.vd-stat-row{display:grid;grid-template-columns:repeat(4,1fr);gap:1rem;margin-bottom:2rem}
.vd-stat{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.5rem;display:flex;flex-direction:column;gap:.35rem;box-shadow:var(--shadow)}
.vd-stat-n{font-size:1.6rem;font-weight:700;color:var(--ink);letter-spacing:-.01em;line-height:1.15}
.vd-stat-u{font-size:.8rem;color:var(--muted);font-weight:500}

/* Panel */
.vd-panel{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.5rem 1.75rem;margin-top:1rem;box-shadow:var(--shadow)}
.vd-panel p{font-size:.92rem;color:#444;line-height:1.6;margin-bottom:.75rem}
.vd-panel p:last-child{margin-bottom:0}
.vd-panel a{color:var(--accent);font-weight:600;text-decoration:none;transition:color .15s}
.vd-panel a:hover{color:#1d4ed8}

/* School cards */
.vd-school-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:1rem}
.vd-school-card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.5rem;box-shadow:var(--shadow)}
.vd-school-card h3{font-size:1.05rem;font-weight:600;color:var(--ink);letter-spacing:-.01em;margin-bottom:.5rem}
.vd-school-meta{display:flex;align-items:center;gap:.5rem;margin-bottom:.6rem;font-size:.82rem;color:var(--muted);font-weight:500}
.vd-ofsted{font-size:.7rem;font-weight:600;padding:.2rem .55rem;border-radius:999px;letter-spacing:.01em}
.vd-ofsted-outstanding{background:#ecfdf5;color:#065f46}
.vd-ofsted-good{background:var(--accent-soft);color:#1e40af}
.vd-school-stats{display:flex;gap:1rem;font-size:.78rem;color:var(--subtle)}

/* Food */
.vd-food-bar{display:flex;gap:.6rem;margin-bottom:2rem;flex-wrap:wrap}
.vd-food-seg{display:flex;align-items:center;gap:.5rem;padding:.5rem .9rem;background:var(--card);border:1px solid var(--line);border-radius:999px;font-size:.82rem;box-shadow:var(--shadow)}
.vd-food-dot{width:8px;height:8px;border-radius:50%}
.vd-food-dot-5{background:#10b981}.vd-food-dot-4{background:#34d399}.vd-food-dot-3{background:#fbbf24}.vd-food-dot-2{background:#fb923c}.vd-food-dot-1{background:#ef4444}
.vd-food-rating{color:var(--muted);font-weight:500}.vd-food-count{font-weight:600;color:var(--ink)}
.vd-pill{display:inline-flex;align-items:center;justify-content:center;width:1.5rem;height:1.5rem;border-radius:6px;font-size:.72rem;font-weight:700;color:#fff}
.vd-pill-5{background:#10b981}.vd-pill-4{background:#34d399}.vd-pill-3{background:#fbbf24}.vd-pill-2{background:#fb923c}.vd-pill-1{background:#ef4444}.vd-pill-0{background:var(--subtle)}

/* Footer */
.vd-ft{border-top:1px solid var(--line);margin-top:auto;padding:0 2rem;background:var(--bg)}
.vd-ft-in{max-width:1120px;margin:0 auto;padding:2.5rem 0;text-align:center}
.vd-ft-logo{display:flex;justify-content:center;margin-bottom:1rem;color:#d1d5db}
.vd-ft-attr{font-size:.78rem;color:var(--muted);line-height:1.8;font-weight:500}
.vd-ft-legal{font-size:.72rem;color:var(--subtle);margin-top:.4rem}
.vd-ft-copy{font-size:.75rem;color:var(--subtle);margin-top:.75rem;font-weight:500}

/* Rail schematic (SBB-style) */
.vd-rail-map{margin-bottom:1.5rem;background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.25rem;box-shadow:var(--shadow)}
.vd-rail-svg{width:100%;height:auto;display:block;font-family:Inter,-apple-system,system-ui,sans-serif}
.vd-rail-line-label{font-size:11px;font-weight:700;letter-spacing:.08em}
.vd-rail-station{transition:opacity .15s}
.vd-rail-station:hover{opacity:.7}
.vd-rail-station-name{font-size:13px;font-weight:600;fill:var(--ink)}
.vd-rail-station-name.selected{fill:#2563eb}
.vd-rail-station-meta{font-size:11px;font-weight:500;fill:var(--muted)}
.vd-station-list{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:.6rem;margin-bottom:2rem}
.vd-station-btn{background:var(--card);border:1px solid var(--line);border-radius:10px;padding:.95rem 1.1rem;text-align:left;cursor:pointer;display:flex;flex-direction:column;gap:.25rem;transition:all .15s;font-family:inherit;box-shadow:var(--shadow)}
.vd-station-btn:hover{border-color:var(--muted);transform:translateY(-1px)}
.vd-station-btn.active{border-color:var(--accent);background:var(--accent-soft);box-shadow:0 0 0 3px rgba(37,99,235,.08)}
.vd-station-name{font-size:.95rem;color:var(--ink);font-weight:600;letter-spacing:-.01em}
.vd-station-meta{font-size:.76rem;color:var(--muted);font-weight:500}

/* Departures */
.vd-deps{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.5rem 1.75rem;box-shadow:var(--shadow)}
.vd-deps-head{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:1rem;padding-bottom:.75rem;border-bottom:1px solid var(--line-soft)}
.vd-deps-head h3{font-size:1.05rem;font-weight:600;color:var(--ink);letter-spacing:-.01em}
.vd-deps-time{font-size:.75rem;color:var(--subtle);font-weight:500}
.vd-deps-loading,.vd-deps-empty{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:1.5rem 1.75rem;color:var(--muted);font-size:.9rem;box-shadow:var(--shadow)}
.vd-deps-empty strong{display:block;color:var(--ink);font-size:1rem;font-weight:600;margin-bottom:.35rem}
.vd-deps-empty p{font-size:.85rem;margin-top:.3rem}
.vd-ontime{color:#059669;font-weight:600}
.vd-delayed{color:#d97706;font-weight:600}
.vd-cancelled{color:#dc2626;font-weight:600}

/* Loading */
.vd-empty{display:flex;align-items:center;justify-content:center;height:100vh;color:var(--subtle);font-size:1rem;font-weight:500}

/* Responsive */
@media(max-width:900px){.vd-grid{grid-template-columns:repeat(2,1fr)}.vd-stat-row{grid-template-columns:repeat(2,1fr)}.vd-cols{grid-template-columns:1fr}}
@media(max-width:600px){.vd-grid{grid-template-columns:1fr}.vd-stat-row{grid-template-columns:1fr}.vd-hdr-nav{display:none}.vd-main{padding:2rem 1.25rem}.vd-hero h1{font-size:1.75rem}.vd-card-value{font-size:1.5rem}.vd-tbl{font-size:.8rem}.vd-tbl th,.vd-tbl td{padding:.5rem .6rem}}
`;

export default VillageDashboard;
